import React, { useState, useContext, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { UserContext } from "../Contexts/LoginContext";
import { RoleContext } from "../Contexts/RoleContext";
import logo from '../static/Crunch Time Jobs.png';
import background from '../static/login_background.jpg';
import '../static/user.css';
import { url } from "../Components/url"; // Renamed for clarity
import { fetchWithAuth } from "../Components/FetchWithAuth";
import { setCsrfToken } from "../Components/csrf";
import { Spinner } from "react-bootstrap";
function Login() {
    const API_BASE_URL = url; // Use the imported URL
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const [usernameInput, setUsernameInput] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [userId, setUserId] = useState(null);

    const { setUsername } = useContext(UserContext);
    const { setRole } = useContext(RoleContext);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setErrorMessage('');

        try {
            setIsLoading(true);
            const response = await fetch(`${API_BASE_URL}/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    username: usernameInput,
                    password,
                }),
            });

            const data = await response.json().catch(() => ({}));

            if (response.ok) {
                if (data.csrf_token) {
                    setCsrfToken(data.csrf_token);
                }
                if (data.requires_mfa) {
                    navigate('/mfa-verify', {
                        replace: true,
                        state: {
                            requires_mfa: true,
                            userId: data.id,
                            methods: data.methods || ['authenticator', 'email', 'backup'],
                        },
                    });
                    return;
                }
                setUserId(data.id);
                setUsername(data.id);
            } else {
                setIsLoading(false);
                setErrorMessage(data.message || 'Invalid credentials');
            }
        } catch (error) {
            setIsLoading(false);
            console.error("Login error:", error);
            setErrorMessage('An error occurred. Please try again.');
        }
    };

    useEffect(() => {
        const fetchDetails = async () => {
            try {
                const res = await fetchWithAuth(`${API_BASE_URL}/get_user/${userId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                });

                if (!res.ok) throw new Error("Unauthorized or failed request");
                const userData = await res.json();
                setRole(userData.role);
                if (userData.role === 'admin') {
                    navigate('/home');
                } else if (
                    userData.role === 'Counsellor'
                    || userData.role === 'Visa Counsellor'
                    || userData.role === 'Visa&Application Counsellor'
                ) {
                    navigate('/new-enquiry');
                } else if (userData.role === 'Receptionist') {
                    navigate('/all-enquiry');
                } else if (userData.role === 'Manager') {
                    navigate('/view-applications');
                } else {
                    // Unknown/missing role — land on a safe default rather than hanging on login
                    navigate('/home');
                }
            } catch (error) {
                console.error("fetchWithAuth user details failed:", error);
                setErrorMessage("Unable to load your account after login. Please try again.");
                setUserId(null);
            } finally {
                setIsLoading(false);
            }
        };

        if (userId) {
            fetchDetails();
        }
    }, [userId]);

    return (
        <div className="login-container">
            <div className="login-background">
                <img src={background} alt="Background" loading="lazy" />
            </div>

            <div className="login-content">
                <div className="login-card">
                    <div className="login-header">
                        <img src={logo} alt="Logo" className="login-logo" />
                        <h1 className="login-title">Welcome Back</h1>
                        <p className="login-subtitle">Sign in to your account to continue</p>
                    </div>

                    <form onSubmit={handleSubmit} className="login-body">
                        <div className="login-form-group">
                            <label htmlFor="username" className="login-form-label">Username</label>
                            <input
                                type="text"
                                id="username"
                                className="login-form-control"
                                placeholder="Enter your username"
                                value={usernameInput}
                                onChange={(e) => setUsernameInput(e.target.value)}
                                required
                            />
                        </div>

                        <div className="login-form-group">
                            <label htmlFor="password" className="login-form-label">Password</label>
                            <div className="login-password-field">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    className="login-form-control"
                                    placeholder="Enter your password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                                <button
                                    type="button"
                                    className="login-password-toggle"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    aria-pressed={showPassword}
                                >
                                    {showPassword ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                            <line x1="1" y1="1" x2="23" y2="23" />
                                        </svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            <div className="d-flex justify-content-end mt-1">
                                <Link to="/forgot-password" className="login-link" style={{ fontSize: '0.875rem' }}>Forgot password?</Link>
                            </div>
                        </div>

                        {errorMessage && (
                            <div className="login-error d-flex align-items-center mt-2 text-danger" style={{ fontSize: '0.875rem' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="me-2" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                {errorMessage}
                            </div>
                        )}

                        <button type="submit" className="btn btn-primary mt-3" style={{ width: '100%', padding: '0.75rem' }}>
                            {isLoading ? <Spinner animation="border" size="sm" /> : 'Sign In'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default Login;
